import { useCallback, useEffect, useRef } from 'react';
import style from './style.module.scss';
import barcodescanner from '../../assets/barcode-scanner.svg';
import { useStore } from '../../lib/hooks/useStore';
import { parseBarcode } from '../../lib/helpers/barcodeParser';
import { AccountLookupRequestType } from '../../lib/typegen/account-lookup-request-type';
import { KeyValue } from '../../lib/typegen/key-value';
import { LookupType } from '../../lib/typegen/lookup-type';
import MessageBox from '../message-box';
import logger from '../../lib/helpers/logger';
import { observer } from 'mobx-react-lite';

interface ScanBarcodeProps {
	imageProps?: AppImageProps | null;
	handleNavigation?: (pageId: string, params?: KeyValue[]) => void;
	lookupType?: LookupType;
}

const ScanBarcode = observer(function ({ imageProps, handleNavigation, lookupType }: ScanBarcodeProps) {
	const {
		configStore: { getString, envDeviceOption, config },
		accountStore: { lookupAccount, barcodeData, clearBarcodeData },
		uiStore: { isLoaderActive, showLoader, hideLoader },
	} = useStore();

	const clickCountRef = useRef(0);
	const clickTimerRef = useRef<number | null>(null);

	const handleLookup = useCallback(
		async (barcode: string | null) => {
			if (!barcode || isLoaderActive || !handleNavigation) {
				return;
			}
			const accountNumber = parseBarcode(barcode.trim(), config?.barcodeParsePattern);
			clearBarcodeData();

			if (lookupType === LookupType.BarcodeWithLastName) {
				console.log('[ScanBarcode] BarcodeWithLastName flow - navigating to lookup-account with accountNumber:', accountNumber);
				handleNavigation('lookup-account', [
					{ key: 'lookupType', value: String(LookupType.BarcodeWithLastName) },
					{ key: 'scannedAccountNumber', value: accountNumber },
				]);
				return;
			}

			try {
				showLoader();
				await lookupAccount(AccountLookupRequestType.Barcode, { accountNumber }, null);
				handleNavigation('select-accounts');
			} catch (error) {
				logger.logBarcodeScanner.error(
					{
						barcodeString: accountNumber,
					},
					'Error processing barcode:',
					error
				);
			} finally {
				hideLoader();
			}
		},
		[clearBarcodeData, config, handleNavigation, hideLoader, isLoaderActive, lookupAccount, showLoader, lookupType]
	);

	useEffect(() => {
		handleLookup(barcodeData);
	}, [barcodeData, handleLookup]);

	const performTestScan = useCallback(async () => {
		if (envDeviceOption === 'never' || envDeviceOption === 'with-fallback') {
			const barcodeFromEnv = import.meta.env.VITE_USE_BARCODE_SCANNER_ACCOUNT || '990990';
			try {
				await handleLookup(barcodeFromEnv);
			} catch (error) {
				logger.debug.error('Error processing barcode from env variable:', error);
			}
		}
	}, [envDeviceOption, handleLookup]);

	const handleTestScanImageClick = () => {
		clickCountRef.current += 1;

		if (clickCountRef.current === 3) {
			performTestScan();
			clickCountRef.current = 0;
			if (clickTimerRef.current) {
				clearTimeout(clickTimerRef.current);
				clickTimerRef.current = null;
			}
		} else {
			if (clickTimerRef.current) {
				clearTimeout(clickTimerRef.current);
			}
			clickTimerRef.current = window.setTimeout(() => {
				clickCountRef.current = 0;
			}, 500);
		}
	};

	return (
		<figure className={style.barcode}>
			<picture className={[style['barcode__image-wrapper'], style['image-wrapper']].join(' ')}>
				<img
					className={style['image-wrapper__image']}
					src={imageProps?.src ?? barcodescanner}
					alt={imageProps?.alt ?? 'barcode scanner'}
					onClick={handleTestScanImageClick}
					style={{ cursor: 'pointer' }}
				/>
			</picture>
			<MessageBox type='info'>{getString('barcode-instructions')}</MessageBox>
		</figure>
	);
});

export default ScanBarcode;
